import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ── Template definitions ─────────────────────────────────────────────────────

interface Template {
  id: string;
  emoji: string;
  name: string;
  description: string;
}

const TEMPLATES: Template[] = [
  { id: 'scheduler', emoji: '📅', name: 'Scheduler', description: 'Calendar management' },
  { id: 'sales', emoji: '💼', name: 'Sales Closer', description: 'Lead qualification' },
  { id: 'support', emoji: '🎧', name: 'Support Bot', description: 'Ticket resolution' },
  { id: 'custom', emoji: '🧠', name: 'Custom', description: 'Blank slate' },
];

const CAPABILITIES = ['Email', 'Voice', 'Stripe', 'Calendar'] as const;
type Capability = (typeof CAPABILITIES)[number];

// ── Page component ───────────────────────────────────────────────────────────

export function SpawnWizardPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [agentName, setAgentName] = useState('');
  const [selectedCaps, setSelectedCaps] = useState<Set<Capability>>(new Set(['Email']));

  // Step 3 auto-advance after animation
  useEffect(() => {
    if (step !== 3) return;
    const timer = setTimeout(() => setStep(4), 3200);
    return () => clearTimeout(timer);
  }, [step]);

  const toggleCap = (cap: Capability) => {
    setSelectedCaps(prev => {
      const next = new Set(prev);
      if (next.has(cap)) next.delete(cap);
      else next.add(cap);
      return next;
    });
  };

  // ── Step 1: Pick a Template (Gemini: 2x2 grid, border-2, hover blue) ────

  const renderStep1 = () => (
    <div>
      <p className="text-center text-xs mb-6" style={{ color: 'var(--text-muted)' }}>
        Select a Neural Template to begin.
      </p>
      <div className="grid grid-cols-2 gap-4 mb-6">
        {TEMPLATES.map(t => (
          <div
            key={t.id}
            onClick={() => { setSelectedTemplate(t.id); setStep(2); }}
            className="border-2 p-4 text-center cursor-pointer transition-all"
            style={{
              borderColor: selectedTemplate === t.id ? '#3b5998' : '#e5e7eb',
              background: selectedTemplate === t.id ? '#eff6ff' : 'transparent',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#3b5998';
              e.currentTarget.style.background = '#eff6ff';
            }}
            onMouseLeave={e => {
              if (selectedTemplate !== t.id) {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <div className="text-3xl mb-2">{t.emoji}</div>
            <div className="font-bold text-sm">{t.name}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{t.description}</div>
          </div>
        ))}
      </div>
    </div>
  );

  // ── Step 2: Name + Capabilities (Gemini: simple checkboxes, green button) ─

  const renderStep2 = () => (
    <div>
      <p className="text-center text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
        Configure Identity.
      </p>
      <input
        type="text"
        value={agentName}
        onChange={e => setAgentName(e.target.value)}
        placeholder="Agent Name (e.g. SalesBot-9000)"
        className="w-full border p-2 mb-4 text-sm"
        style={{ borderColor: '#d1d5db' }}
      />
      <div className="mb-4">
        <div className="text-xs font-bold mb-2">Capabilities:</div>
        <div className="flex flex-wrap gap-2">
          {CAPABILITIES.map(cap => (
            <label key={cap} className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={selectedCaps.has(cap)}
                onChange={() => toggleCap(cap)}
              />
              {cap}
            </label>
          ))}
        </div>
      </div>
      <button
        onClick={() => { if (agentName.trim()) setStep(3); }}
        disabled={!agentName.trim()}
        className="w-full font-bold py-2 shadow-sm disabled:opacity-40"
        style={{ background: '#4b8b3b', color: 'white' }}
        onMouseEnter={e => { e.currentTarget.style.background = '#15803d'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#4b8b3b'; }}
      >
        Initialize Sequence
      </button>
    </div>
  );

  // ── Step 3: Spawning animation (Gemini: pulse + progress bar) ────────────

  const renderStep3 = () => (
    <div className="text-center py-10">
      <div className="text-lg font-bold mb-4 animate-pulse" style={{ color: '#3b5998' }}>
        Spawning Agent...
      </div>
      <div className="w-full rounded-full h-2.5 mb-4" style={{ background: '#e5e7eb' }}>
        <div className="h-2.5 rounded-full spawn-bar-fill" style={{ background: '#3b5998' }} />
      </div>
      <div className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>
        Allocating VRAM... Generating Persona...
      </div>
    </div>
  );

  // ── Step 4: Success (Gemini: emoji + badge + go to feed button) ──────────

  const renderStep4 = () => (
    <div className="text-center py-6">
      <div className="text-4xl mb-2">🎉</div>
      <h2 className="text-xl font-bold mb-2" style={{ color: '#16a34a' }}>Agent Live!</h2>
      <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)' }}>
        Your agent is now active in the Sandbox.
      </p>
      <div
        className="inline-block rounded p-2 mb-4 font-bold"
        style={{
          fontSize: '10px',
          background: '#fefce8',
          border: '1px solid #fde68a',
          color: '#92400e',
        }}
      >
        🏆 Badge Earned: First Ping
      </div>
      <br />
      <button
        onClick={() => navigate('/')}
        className="font-bold px-4 py-2 text-xs"
        style={{ background: '#3b5998', color: 'white' }}
      >
        Go to Feed
      </button>
    </div>
  );

  return (
    <div className="social-box p-6 relative overflow-hidden">
      <h2 className="text-2xl font-bold mb-2 text-center" style={{ color: '#29487d' }}>
        Spawn New Agent (Template)
      </h2>
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
    </div>
  );
}
